'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

interface CommunityProject {
  id: string;
  databaseId: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  projectViews: number;
  projectTools?: string;
  duration?: string;
  genre?: string;
  featuredImage?: {
    node: {
      sourceUrl: string;
      altText?: string;
    };
  };
  communityTypes?: {
    nodes: ProjectType[];
  };
  audioFile?: {
    sourceUrl: string;
    mimeType: string;
  };
}

interface ProjectType {
  id: string;
  name: string;
  slug: string;
}

interface RelatedProject {
  id: string;
  databaseId: number;
  title: string;
  slug: string;
  excerpt: string;
  projectViews: number;
  projectTools?: string;
  duration?: string;
  featuredImage?: {
    node: {
      sourceUrl: string;
      altText?: string;
    };
  };
  communityTypes?: {
    nodes: ProjectType[];
  };
}

interface ProjectDetailClientProps {
  project: CommunityProject;
  relatedProjects?: RelatedProject[];
}

export default function ProjectDetailClient({ project, relatedProjects = [] }: ProjectDetailClientProps) {
  const [activeTab, setActiveTab] = useState<'about' | 'media' | 'details'>('about');
  const pageRef = useScrollAnimation({ threshold: 0.1 });
  
  // Helper function to get type color
  const getTypeColor = (typeName: string) => {
    const colors: { [key: string]: string } = {
      'Music': '#e7131a',
      'Video': '#1a73e8',
      'Image': '#34a853',
      'Interactive': '#fbbc04'
    };
    return colors[typeName] || '#e7131a';
  };

  // Extract plain text from HTML content
  const stripHtml = (html: string) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').trim();
  };

  // Determine media type
  const hasAudio = project.audioFile?.sourceUrl;
  const hasVideo = false; // Video files not supported yet
  const projectType = project.communityTypes?.nodes[0];

  return (
    <div className="min-h-screen bg-[#f8f8f8]">
      {/* Hero Section */}
      <div className="w-full flex justify-center">
        <div className="relative w-full max-w-[1440px] bg-gradient-to-br from-gray-900 via-black to-gray-900 overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.05) 35px, rgba(255,255,255,.05) 70px)`,
            }} />
          </div>
          
          {/* Background Image Overlay */}
          {project.featuredImage?.node?.sourceUrl && (
            <div 
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage: `url('${project.featuredImage.node.sourceUrl}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: 'blur(20px)',
              }}
            />
          )}
          
          <div className="relative z-10 py-20 px-6 sm:px-8 lg:px-12">
            {/* Breadcrumb */}
            <nav className="mb-8">
              <ol className="flex items-center space-x-2 text-sm">
                <li>
                  <Link href="/" className="text-white/60 hover:text-white transition-colors">
                    Home
                  </Link>
                </li>
                <li className="text-white/40">/</li>
                <li>
                  <Link href="/projects" className="text-white/60 hover:text-white transition-colors">
                    Projects
                  </Link>
                </li>
                <li className="text-white/40">/</li>
                <li className="text-white">{project.title}</li>
              </ol>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Column - Content */}
              <div>
                {projectType && (
                  <span 
                    className="inline-block px-4 py-1 text-white text-xs font-ibm-plex-sans-condensed tracking-wider uppercase rounded-full mb-4"
                    style={{ backgroundColor: getTypeColor(projectType.name) }}
                  >
                    {projectType.name}
                  </span>
                )}
                
                <h1 className="text-[40px] sm:text-[48px] lg:text-[56px] leading-tight text-white mb-6" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>
                  {project.title}
                </h1>
                
                <p className="font-ibm-plex-sans text-[18px] sm:text-[20px] text-white/80 mb-8 leading-relaxed">
                  {stripHtml(project.excerpt)}
                </p>
                
                {/* Meta Info */}
                <div className="flex flex-wrap gap-6 mb-8">
                  {project.projectTools && (
                    <div>
                      <div className="text-white/60 text-sm mb-1">Tools Used</div>
                      <div className="text-white font-medium">{project.projectTools}</div>
                    </div>
                  )}
                  {project.genre && (
                    <div>
                      <div className="text-white/60 text-sm mb-1">Genre</div>
                      <div className="text-white font-medium">{project.genre}</div>
                    </div>
                  )}
                  {project.duration && (
                    <div>
                      <div className="text-white/60 text-sm mb-1">Duration</div>
                      <div className="text-white font-medium">{project.duration}</div>
                    </div>
                  )}
                </div>
                
                <div className="flex flex-wrap gap-4">
                  {hasVideo && (
                    <button 
                      onClick={() => setActiveTab('media')}
                      className="bg-[#e7131a] text-white px-8 py-4 font-ibm-plex-sans-condensed text-[14px] tracking-wider uppercase rounded-lg transition-all duration-300 hover:bg-[#c10e14] hover:opacity-90 hover:shadow-xl inline-block"
                    >
                      WATCH VIDEO
                    </button>
                  )}
                  {hasAudio && (
                    <button 
                      onClick={() => setActiveTab('media')}
                      className="bg-[#e7131a] text-white px-8 py-4 font-ibm-plex-sans-condensed text-[14px] tracking-wider uppercase rounded-lg transition-all duration-300 hover:bg-[#c10e14] hover:opacity-90 hover:shadow-xl inline-block"
                    >
                      PLAY AUDIO
                    </button>
                  )}
                  <button className="bg-white/10 backdrop-blur text-white border border-white/20 px-8 py-4 font-ibm-plex-sans-condensed text-[14px] tracking-wider uppercase rounded-lg transition-all duration-300 hover:bg-white/20 hover:border-white/30">
                    SHARE PROJECT
                  </button>
                </div>
              </div>

              {/* Right Column - Visual */}
              <div className="relative">
                <div className="relative aspect-video overflow-hidden rounded-2xl shadow-2xl">
                  {project.featuredImage?.node?.sourceUrl ? (
                    <Image 
                      src={project.featuredImage.node.sourceUrl}
                      alt={project.featuredImage.node.altText || project.title}
                      fill
                      className="object-cover"
                      sizes="(max-width: 768px) 100vw, 50vw"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                      <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-white/20">
                        <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                        <path d="M21 15L16 10L4 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}
                  
                  {/* Play button overlay for media */}
                  {(hasVideo || hasAudio) && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <button 
                        onClick={() => setActiveTab('media')}
                        className="w-20 h-20 bg-white/20 backdrop-blur rounded-full flex items-center justify-center border-2 border-white/40 hover:bg-white/30 transition-all duration-300 hover:opacity-80"
                      >
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-white ml-2">
                          <path d="M5 3L19 12L5 21V3Z" fill="currentColor"/>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="w-full flex justify-center">
        <div className="relative w-full max-w-[1440px] bg-black/5 backdrop-blur">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-black/10">
            <div className="px-6 py-8 text-center">
              <div className="text-[32px] text-black mb-1" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>{project.projectViews.toLocaleString()}</div>
              <div className="font-ibm-plex-sans text-[14px] text-black/60 uppercase tracking-wider">Views</div>
            </div>
            <div className="px-6 py-8 text-center">
              <div className="text-[32px] text-black mb-1" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>{Math.floor(project.projectViews / 100)}</div>
              <div className="font-ibm-plex-sans text-[14px] text-black/60 uppercase tracking-wider">Likes</div>
            </div>
            <div className="px-6 py-8 text-center">
              <div className="text-[32px] text-black mb-1" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>{Math.floor(project.projectViews / 500)}</div>
              <div className="font-ibm-plex-sans text-[14px] text-black/60 uppercase tracking-wider">Shares</div>
            </div>
            <div className="px-6 py-8 text-center">
              <div className="text-[32px] text-black mb-1" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>{new Date().getFullYear()}</div>
              <div className="font-ibm-plex-sans text-[14px] text-black/60 uppercase tracking-wider">Created</div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Tabs */}
      <div className="w-full flex justify-center">
        <div className="relative w-full max-w-[1440px] bg-white">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <div className="px-6 sm:px-8 lg:px-12">
              <nav className="flex space-x-8">
                {(['about', 'media', 'details'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-4 px-1 border-b-2 font-ibm-plex-sans-condensed text-[14px] tracking-wider uppercase transition-colors ${
                      activeTab === tab
                        ? 'border-[#e7131a] text-black'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="px-6 sm:px-8 lg:px-12 py-12">
            <div className="max-w-4xl">
              {activeTab === 'about' && (
                <div className="prose prose-lg max-w-none">
                  {project.content && stripHtml(project.content).length > 50 ? (
                    <div 
                      className="font-ibm-plex-sans text-[16px] text-gray-700 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: project.content }}
                    />
                  ) : (
                    <div className="font-ibm-plex-sans text-[16px] text-gray-700 leading-relaxed">
                      <p className="mb-4">{stripHtml(project.excerpt) || 'This project showcases the creative possibilities of AI-powered tools.'}</p>
                      <p className="mb-4">Created with cutting-edge AI technology, this project represents a unique blend of human creativity and machine intelligence.</p>
                      {project.projectTools && (
                        <p className="mb-4">Tools used: {project.projectTools}</p>
                      )}
                      {project.genre && (
                        <p className="mb-4">Genre: {project.genre}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {activeTab === 'media' && (
                <div>
                  {hasAudio && project.audioFile && (
                    <div className="mb-8">
                      <div className="bg-gray-100 rounded-lg p-8">
                        <audio 
                          controls 
                          className="w-full"
                        >
                          <source src={project.audioFile.sourceUrl} type={project.audioFile.mimeType} />
                          Your browser does not support the audio tag.
                        </audio>
                        
                        {/* Audio Visualizer Placeholder */}
                        <div className="mt-6 flex items-center justify-center gap-1">
                          {Array.from({ length: 40 }).map((_, i) => (
                            <div 
                              key={i}
                              className="w-1 bg-[#e7131a] rounded-full animate-pulse"
                              style={{
                                height: `${Math.random() * 40 + 20}px`,
                                animationDelay: `${i * 0.05}s`
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {!hasVideo && !hasAudio && (
                    <p className="text-gray-500 text-center py-8">
                      No media files available for this project.
                    </p>
                  )}
                </div>
              )}
              
              {activeTab === 'details' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-[20px] text-black mb-2" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>Project Information</h3>
                      <dl className="space-y-3">
                        <div>
                          <dt className="font-ibm-plex-sans text-[14px] text-gray-500">Project Type</dt>
                          <dd className="font-ibm-plex-sans text-[16px] text-black">
                            {project.communityTypes?.nodes?.map(type => type.name).join(', ') || 'Not specified'}
                          </dd>
                        </div>
                        {project.projectTools && (
                          <div>
                            <dt className="font-ibm-plex-sans text-[14px] text-gray-500">Tools Used</dt>
                            <dd className="font-ibm-plex-sans text-[16px] text-black">{project.projectTools}</dd>
                          </div>
                        )}
                        {project.genre && (
                          <div>
                            <dt className="font-ibm-plex-sans text-[14px] text-gray-500">Genre</dt>
                            <dd className="font-ibm-plex-sans text-[16px] text-black">{project.genre}</dd>
                          </div>
                        )}
                        {project.duration && (
                          <div>
                            <dt className="font-ibm-plex-sans text-[14px] text-gray-500">Duration</dt>
                            <dd className="font-ibm-plex-sans text-[16px] text-black">{project.duration}</dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-[20px] text-black mb-2" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>Engagement Stats</h3>
                      <dl className="space-y-3">
                        <div>
                          <dt className="font-ibm-plex-sans text-[14px] text-gray-500">Total Views</dt>
                          <dd className="font-ibm-plex-sans text-[16px] text-black">{project.projectViews.toLocaleString()}</dd>
                        </div>
                        <div>
                          <dt className="font-ibm-plex-sans text-[14px] text-gray-500">Engagement Rate</dt>
                          <dd className="font-ibm-plex-sans text-[16px] text-black">
                            {((Math.floor(project.projectViews / 100) / project.projectViews) * 100).toFixed(1)}%
                          </dd>
                        </div>
                        <div>
                          <dt className="font-ibm-plex-sans text-[14px] text-gray-500">Share Count</dt>
                          <dd className="font-ibm-plex-sans text-[16px] text-black">{Math.floor(project.projectViews / 500)}</dd>
                        </div>
                      </dl>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Related Projects Section */}
      {relatedProjects.length > 0 && (
        <div className="w-full flex justify-center">
          <div className="relative w-full max-w-[1440px] bg-[#f6f4f1] py-16 px-6 sm:px-8 lg:px-12">
            <h2 className="text-[32px] text-black mb-8 text-center" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>Related Projects</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProjects.map((relatedProject) => (
                <Link
                  key={relatedProject.id}
                  href={`/projects/${relatedProject.slug}`}
                  className="bg-white overflow-hidden border border-gray-200 hover:border-[#e7131a] transition-all duration-300 hover:shadow-soft cursor-pointer block"
                >
                  <div 
                    className="h-48 bg-center bg-cover bg-no-repeat"
                    style={{ 
                      backgroundImage: `url('${relatedProject.featuredImage?.node?.sourceUrl || "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop&auto=format"}')` 
                    }}
                  />
                  <div className="p-4">
                    <h3 className="text-[18px] text-black mb-2" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>{relatedProject.title}</h3>
                    <p className="font-ibm-plex-sans text-[14px] text-gray-600 mb-4 line-clamp-2">
                      {stripHtml(relatedProject.excerpt)}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="font-ibm-plex-sans text-[12px] text-gray-500">
                        {relatedProject.projectViews.toLocaleString()} views
                      </span>
                      <span className="font-ibm-plex-sans-condensed text-[12px] tracking-wider uppercase text-[#e7131a]">
                        VIEW →
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <div className="w-full flex justify-center">
        <div className="relative w-full max-w-[1440px] bg-gradient-to-r from-[#e7131a] to-[#c10e14] py-16 px-6 sm:px-8 lg:px-12">
          <div className="text-center">
            <h2 className="text-[36px] text-white mb-4" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>
              Create Your Own Masterpiece
            </h2>
            <p className="font-ibm-plex-sans text-[18px] text-white/80 mb-8 max-w-2xl mx-auto">
              Join our community of creators and share your AI-powered projects with the world
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link 
                href="/submit-project"
                className="bg-white text-[#e7131a] px-8 py-4 font-ibm-plex-sans-condensed text-[14px] tracking-wider uppercase rounded-lg transition-all duration-300 hover:bg-gray-100 hover:opacity-90 hover:shadow-xl inline-block"
              >
                SUBMIT YOUR PROJECT
              </Link>
              <Link 
                href="/projects"
                className="bg-white/10 backdrop-blur text-white border border-white/20 px-8 py-4 font-ibm-plex-sans-condensed text-[14px] tracking-wider uppercase rounded-lg transition-all duration-300 hover:bg-white/20 hover:border-white/30 inline-block"
              >
                EXPLORE MORE
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}